import { useTranslation } from "react-i18next";

import { peekLine, UNASKED, type RoomPeek } from "@/engine/state/roomPeek";
import { roomName } from "@/engine/state/roomIdentity";
import { useHydrated } from "@/hooks/useHydrated";
import { cn } from "@/engine/support/classNames";

export interface EntrySceneProps {
  /** What was found behind the code being typed, if anything was asked. */
  peek?: RoomPeek;
  /** The code that was asked about, so an answer can be given a name. */
  code?: string;
}

/**
 * The words the way in says about the room behind a code.
 *
 * No picture of the place: home draws the clearing where you are already
 * standing, and a strip above a form would draw a place nobody has entered
 * yet. What it says is the answer itself — what the place is called, and how
 * many are standing at it.
 *
 * What it cannot report is who is speaking: that is measured from a peer's own
 * audio, which a visitor who has not entered does not have. So nothing here
 * claims a voice is at the fire.
 */
export function EntryScene({ peek = UNASKED, code = "" }: EntrySceneProps) {
  const { t } = useTranslation();
  const line = peekLine(peek);
  // The name rides with the answer rather than with the code. A code in hand is
  // not a room: dressing one that answered nothing up in title case would be
  // the strip claiming a place exists because somebody typed its address.
  const name = line ? roomName(code) : "";

  return (
    /* Short screens drop it rather than shrink it. The page is `h-dvh` with
       `overflow-hidden`, so what the strip takes comes off the card the
       visitor came here to use. 700px is this strip's own threshold, and the
       only one on the screen. A narrow-screen size of its own is #443. */
    <div
      data-testid="entry-scene"
      data-peek={peek.kind}
      className="hidden [@media(min-height:700px)]:block w-full mb-8"
    >
      {name && (
        <p
          data-testid="entry-scene-name"
          className="text-center text-sm font-medium text-foreground"
        >
          {name}
        </p>
      )}
      {line && (
        <p
          data-testid="entry-scene-line"
          className={cn(
            "text-center text-xs text-muted-foreground",
            // The count sits under the name when there is one, and keeps a gap
            // of its own from the card above when there is not.
            name ? "mt-0.5" : "mt-2",
          )}
        >
          {t(line.key, { count: line.count })}
        </p>
      )}
    </div>
  );
}

/**
 * The strip where it has something to report: a code in hand. With none, the
 * brandbook's home has no strip, and the character card is the screen's face.
 */
export function CodeScene({ peek, code }: { peek: RoomPeek; code: string }) {
  // A code remembered from the last room is the browser's, and the server
  // drew no strip for it: drawn before hydration the two disagree and React
  // throws the page away (#791).
  const hydrated = useHydrated();
  return code && hydrated ? <EntryScene peek={peek} code={code} /> : null;
}
